import SectionMessage from '@atlaskit/section-message';
import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { State } from 'model/State';
import { selectTrackedElsewhere } from 'modules/mac/duck/selectors';
import {
  StyledFlexWrapper,
  StyledNewTrialOfferWrapper,
  StyledTableData,
  StyledTDSection,
} from './StyledComponents';
import { messages } from '../../messages';
import ContactsSection from './DetailsRowContentElements/licensesRowContacts';
import NonPubliclyAvailableAddOnMessage from './NonPubliclyAvailableAddOnMessage';
import ActionsSection from './ActionsSection';
import { LICENSE_EVALUATION } from 'model/hams/LicenseType';
import MainProductDetailsSection from './DetailsRowContentElements/MainProductDetailsSection';
import BTFProductDetails from './DetailsRowContentElements/BTFProductDetails';
import HistorySection from './DetailsRowContentElements/HistorySection';
import { CloudMigrationTrialOfferBanner } from '../endUser/CloudMigrationTrialOfferBanner';
import { AccountRenewalAction } from '../../../../util/mac/enums';
import { isAdvisoryServicesProduct, isSupportOnlyProductKey } from '../../../../util/mac/helpers';
import { removeTimezone } from 'modules/mac/common/utils';

const DetailsRowContent = ({
  accountDetails,
  billingContacts,
  technicalContacts,
  isAccount,
  isOpen,
  openModal,
  closeModal,
  isArchived,
}) => {
  const productPackage = accountDetails.merchandise.productPackage || accountDetails.productPackage;
  const { key, addOn, publiclyAvailable, onDemand } = productPackage;
  const {
    merchandise: { licenseType },
    id: accountId,
    entSubMigrated,
  } = accountDetails;

  const isEvaluation = licenseType === LICENSE_EVALUATION;
  const isOnDemand = onDemand;

  const showContacts =
    !isAdvisoryServicesProduct(key) && (!isEvaluation || isOnDemand) && !entSubMigrated;

  const isTrackedElsewhere: boolean = useSelector((state) =>
    selectTrackedElsewhere(state as State, accountDetails.id),
  );

  const showLicenseEndDateBanner =
    productPackage?.isBTFSubscription &&
    !isSupportOnlyProductKey(key) &&
    !isAdvisoryServicesProduct(key) &&
    (accountDetails.renewalAction !== AccountRenewalAction.AUTO_RENEW ||
      accountDetails.creditCardData === null);

  const isNonPubliclyAvailableAddOn = addOn && !publiclyAvailable;

  return (
    <StyledTableData $paddingLeft colSpan={5}>
      <StyledFlexWrapper>
        <StyledTDSection width="60%">
          <MainProductDetailsSection accountDetails={accountDetails} />
          {showLicenseEndDateBanner && (
            <SectionMessage appearance="information">
              <FormattedMessage
                {...messages.updateEndDateMessage}
                values={{
                  endDate: (
                    <FormattedDate
                      value={removeTimezone(accountDetails.latestPeriod.endDate)}
                      day="2-digit"
                      month="long"
                      year="numeric"
                    />
                  ),
                }}
              />
            </SectionMessage>
          )}
          <ContactsSection
            technicalContacts={technicalContacts}
            billingContacts={billingContacts}
            showContacts={showContacts}
            accountId={accountId}
            isOpen={isOpen}
            openModal={openModal}
            closeModal={closeModal}
          />
        </StyledTDSection>
        {isTrackedElsewhere === false && (
          <>
            {isNonPubliclyAvailableAddOn && (
              <SectionMessage>
                <NonPubliclyAvailableAddOnMessage accountDetails={accountDetails} />
              </SectionMessage>
            )}
            <StyledTDSection width="40%">
              <BTFProductDetails accountDetails={accountDetails} />
              <ActionsSection
                isAccount={isAccount}
                accountDetails={accountDetails}
                isArchived={isArchived}
              />
              <HistorySection accountDetails={accountDetails} productKey={key} />
              <StyledNewTrialOfferWrapper>
                <CloudMigrationTrialOfferBanner account={accountDetails} />
              </StyledNewTrialOfferWrapper>
            </StyledTDSection>
          </>
        )}
      </StyledFlexWrapper>
    </StyledTableData>
  );
};

export default DetailsRowContent;
